/* eslint-disable no-console */
import ms from 'ms';
import chalk from 'chalk';
import * as fs from 'node:fs';
import * as url from 'node:url';
import * as _ from 'lodash';
import * as path from 'node:path';
import * as utils from './utils';
import * as runOptionsUtils from './runOptionsUtils';
import * as analytics from './commons/testimAnalytics';
import * as runOptionsAgentFlow from './runOptionsAgentFlow';
import * as localRunnerCache from './commons/runnerFileCache';
import { Command } from 'commander';
import { CLI_MODE, REGION_CONFIG, OPERANDS } from './commons/constants';
import { NoArgsError, ArgError } from './errors';
import { EDGE_CHROMIUM_MIN_VERSION } from './player/constants';
import { replaceArgsWithNoDashes } from './utils';
import { featureFlagsService } from './commons/featureFlagsService';
import { secretsUtils } from './commons/secretsUtils';
import { extensionBrowsers, seleniumBrowsers } from './runners/runnerUtils';
import { getRegionFromProjectId } from './utils/regionUtils';
import { setServicesHost, RUNNER_ENVIRONMENT, BETTERSTACK_INGEST_URL_US, BETTERSTACK_TOKEN_US, BETTERSTACK_INGEST_URL_EU, BETTERSTACK_TOKEN_EU } from './commons/config';
import { checkForbiddenKeys } from './services/customCapabilitiesService';
import { setBetterStackCredentials } from './commons/logger';
import type { RunnerMode } from './runners/runnerUtils';
import type { InitializedUserWithAuth } from './commons/testimServicesApi';
import type { SerializedGrid } from './services/gridService';
import type { TestRunStatusTestResult } from './testRunStatus';
import type { UserData } from 'clickim/src/background/session/sessionPlayer';
import type { ICapabilities } from 'services/src/commons/mongo/models/customCapabilities';
import type { TestOptimization } from '../../services/src/commons/mongo/models/testOptimization.ts';

const camelizeHyphenValues = (prop: string) => prop.replace(/-([a-z])/g, (m, w) => w.toUpperCase());

const collect = (val: string, col: string[]) => {
    col.push(val);
    return col;
};

const createDuplicateChecker =
    (opt: string) =>
    (value: string, previous?: string): string => {
        if (previous !== undefined) {
            throw new ArgError(`The ${opt} flag can only be specified once.`);
        }
        return value;
    };

const cli = new Command();

const list = (val: string) => val.split(',');

const mergeValues = (first: any, second: any) => (!first || first.length === 0 ? second : first);

const allowedChromeFlags = [
    'enable-heavy-ad-intervention',
    'heavy-ad-privacy-mitigations',
    'use-fake-device-for-media-stream',
    'use-fake-ui-for-media-stream',
    'proxy-server',
];

const printUsage = () => {
    function isDefaultHelpLine(line: string) {
        return line.includes('-h, --help');
    }

    function isParamsJsonOption(line: string) {
        return line.includes('--params [params-json-string]');
    }

    function isExtOnlyOption(line: string) {
        return (
            line.includes('--ext') ||
            line.includes('--extension-path') ||
            line.includes('--headless-extension-download-path')
        );
    }

    function isPlayerOption(line: string) {
        return line.includes('--player-path') || line.includes('--player-require-path');
    }

    function isScheduler(line: string) {
        return (
            line.includes('--executionId') ||
            line.includes('--source') ||
            line.includes('--resultId') ||
            line.includes('--remoteRunId') ||
            line.includes('--schedulerId')
        );
    }

    function isWebdriverTimeout(line: string) {
        return (
            line.includes('--get-browser-timeout') ||
            line.includes('--get-browser-retries') ||
            line.includes('--get-session-timeout') ||
            line.includes('--get-session-retries') ||
            line.includes('--driver-request-timeout') ||
            line.includes('--driver-request-retries')
        );
    }

    function isUserId(line: string) {
        return line.includes('--user');
    }

    function isMonitorPerformance(line: string) {
        return line.includes('shouldMonitorPerformance');
    }

    function isSaveRCALocally(line: string) {
        return line.includes('--save-rca-locally');
    }

    function isExitCodeIgnoreFailingTests(line: string) {
        return line.includes('--exit-code-ignore-failing-tests');
    }

    function isDeprecatedHighSpeed(line: string) {
        return line.includes('--high-speed'); // high speed mode was renamed to turbo mode
    }

    function isUrls(line: string) {
        return line.includes('--urls');
    }

    function isTestStartTimeout(line: string) {
        return line.includes('--test-start-timeout');
    }

    function isInstallLazyDeps(line: string) {
        return line.includes('--install-lazy-deps');
    }

    function isPrintGridsOrCaps(line: string) {
        return line.includes('--print-final-caps') || line.includes('--print-grids');
    }

    cli.help((txt) => {
        const lines = txt.split('\n');
        return lines
            .filter(
                // hide CLI options
                (ln) =>
                    !isExtOnlyOption(ln) &&
                    !isParamsJsonOption(ln) &&
                    !isDefaultHelpLine(ln) &&
                    !isPlayerOption(ln) &&
                    !isScheduler(ln) &&
                    !isMonitorPerformance(ln) &&
                    !isUserId(ln) &&
                    !isWebdriverTimeout(ln) &&
                    !isSaveRCALocally(ln) &&
                    !isExitCodeIgnoreFailingTests(ln) &&
                    !isDeprecatedHighSpeed(ln) &&
                    !isUrls(ln) &&
                    !isTestStartTimeout(ln) &&
                    !isInstallLazyDeps(ln) &&
                    !isPrintGridsOrCaps(ln),
            )
            .join('\n');
    });
};

const printDeprecationWarning = (deprecatedUsage: string, newUsage: string) => {
    const newUsageString = newUsage ? `Please use ${newUsage} instead.` : '';
    console.log(chalk.yellow(`\nWARNING: ${deprecatedUsage} is deprecated. ${newUsageString}\n`));
};

const CODE_COVERAGE_REPORTER_OPTIONS = [
    'clover',
    'html',
    'json-summary',
    'json',
    'lcov',
    'lcovonly',
    'teamcity',
    'text-lcov',
    'text-summary',
    'text',
];

const { env, argv, exit, cwd } = global.process;

const betterStackConfigMap = {
        usw2: { ingestUrl: BETTERSTACK_INGEST_URL_US, token: BETTERSTACK_TOKEN_US },
        euc1: { ingestUrl: BETTERSTACK_INGEST_URL_EU, token: BETTERSTACK_TOKEN_EU }
}

const regionConfigsMaps = {
    development: {
        usw2: { servicesUrl: 'https://services.dev.testim.io' },
        euc1: { servicesUrl: 'https://services.dev.eu.testim.io' },
    },
    staging: {
        usw2: { servicesUrl: 'https://services.staging.testim.io' },
        euc1: { servicesUrl: 'https://services.staging.eu.testim.io' },
    },
    production: {
        usw2: { servicesUrl: 'https://services.testim.io' },
        euc1: { servicesUrl: 'https://services.eu.testim.io' },
    },
};

const setRegionConfig = async (projectId: string) => {
    // TESTIM_REGION is set when starting pod from services, and is used to determine the region prefix for region-shared projects (e.g. demo projects).
    const region = getRegionFromProjectId(projectId);
    const cachedRegionConfig = await localRunnerCache.get(REGION_CONFIG.CACHE_FN_NAME);

    // For internal users that might run tests from different regions, we want to check the region before extracting it from cache.
    if (cachedRegionConfig && cachedRegionConfig.region === region) {
        setServicesHost(cachedRegionConfig.servicesUrl);
        return;
    }

    const regionConfigMap =
        regionConfigsMaps[RUNNER_ENVIRONMENT as keyof typeof regionConfigsMaps] || regionConfigsMaps.production;
    const regionConfig = regionConfigMap[region!] || {
        servicesUrl: 'https://services.testim.io',
    };
    // If the user specified a services host, we will not override it (example - local debugging for localhost:8080).
    if (!env.SERVICES_HOST) {
        setServicesHost(regionConfig.servicesUrl);
    }
    setBetterStackCredentials(betterStackConfigMap[region!].ingestUrl || BETTERSTACK_INGEST_URL_US, betterStackConfigMap[region!].token || BETTERSTACK_TOKEN_US);
};

const LOG_LEVEL_OPTIONS = new Set(['trace', 'debug', 'info', 'warn', 'error', 'silent']);

replaceArgsWithNoDashes(argv, new Set(['run', 'connect', 'agent', 'tunneld', 'start', 'init', 'install-lazy-deps']));

cli.description('Testim.io CLI')
    .option('-h --help', 'output usage information', printUsage)
    .option('-o --options-file [options-file.json]', '')
    .option('-c --config-file [config-file.js]', '')
    .option('--test-data [test-data]', 'test data file name to include in the test run')
    .option(
        '--test-config [test-config]',
        'test config name to override for all tests in current execution',
        collect,
        [],
    )
    .option(
        '--test-config-id [test-config-id]',
        'test config ID to override for all tests in current execution',
        collect,
        [],
    )
    .option('--params-file [params-file.json | params-file.js]', '')
    .option('--params [params-json-string]', '')
    .option('-t, --testId [test-id]', 'test ID to run', collect, [])
    .option('--test-id [test-id]', 'test ID to run', collect, [])
    .option('-l, --label [label]', 'labels to search test by', collect, [])
    .option('-n, --name [test-name]', 'test name to run', collect, [])
    .option('--project [project-id]', 'project ID')
    .option('-r, --report-file [report junit xml path]', 'where to save junit xml results file')
    .option('--urls', 'add step urls to the junit report')
    .option(
        '--override-report-file-classname [override-report-file-classname]',
        'custom junit class name for the junit reporter',
    )
    .option('--reporters [names]', 'report types', list)
    .option('-h, --host [host-name]', 'host name or ip containing the selenium grid')
    .option('-p, --port [host-port]', 'host port')
    .option('-g, --grid [grid-name]', 'grid name')
    .option('--path [grid-path]', 'grid path')
    .option('--protocol [grid-protocol]', 'grid protocol http or https')
    .option('--grid-username [grid-username]', 'grid http basic auth username')
    .option('--grid-password [grid-password]', 'grid http basic auth password')
    .option('-gi --grid-id [grid-id]', 'grid ID')
    .option('-b, --browser [browser-type]', 'browser type (chrome/firefox)')
    .option('-h, --headless [headless]', 'run in headless mode')
    .option('-m, --mode [runner-mode]', 'use extension or selenium mode (extension/selenium/appium)')
    .option('--branch [branch]', 'branch name', undefined)
    .option('--base-url [base-url]', 'change base-url to a specified url for all tests in current execution')
    .option('--token [token]', 'identification token to testim')
    .option('--parallel [number-of-tests]', 'number of tests to run in parallel')
    .option(
        '--before-parallel [number-of-tests]',
        'number of tests to run in parallel during the before phase of a test plan',
    )
    .option(
        '--after-parallel [number-of-tests]',
        'number of tests to run in parallel during the after phase of a test plan',
    )
    .option('--canary [canary-mode]', 'enable canary mode', false)
    .option('--test-plan [test-plan-name]', 'test plan to run', collect, [])
    .option('--test-plan-id [test-plan-id]', 'test plan to run', collect, [])
    .option('--suite [suite-name]', 'suite to run', collect, [])
    .option('--suite-id [suite-id]', 'suite ID to run', collect, [])
    .option('--rerun-failed-by-run-id [run-id]', 'allows re-running failed tests from a specific run ID')
    .option('--disable-grid-check [boolean]', 'disable checking if selenium grid is available', false)
    .option(
        '--disable-native-events [boolean]',
        'pass nativeEvents=false capability to the selenium browser (in selenium mode)',
        false,
    )
    .option('--disable-timeout-retry [boolean]', 'disable retry after test pass test timeout', false)
    .option('--ca-file [ca-file-location]', 'ca certificate file location')
    .option('--proxy [proxy-url]', 'proxy url to all requests')
    .option(
        '--proxy-for-grid [proxy-for-grid]',
        'used together with --proxy to also router grid traffic through a proxy',
    )
    .option('--result-label [result-label]', 'result label', collect, [])
    .option('-oen --override-execution-name [execution-name]', 'override the default execution name', '')
    .option(
        '--retries [max_num_of_retries]',
        'number of retries on test failure, defaults to 0 (no retries)',
        Number,
        0,
    )
    .option('--set-retention [retention-in-days]', 'set the number of days for results retention')
    .option('--user [user-id]', 'user ID for local Testim-CLI')
    .option('--user-access-key [user-access-key]', 'EXPERIMENTAL') //TODO: add description (TES-18133)
    .option('--pass-zero-tests', "don't fail the run if no tests were found")
    //Mobile options
    .option('--device-name [device-name]', 'The device name to use, iPhone 12, Nexus 5X, SM-G900P etc....')
    .option('--device-udid [device-udid]', 'Unique Device Identifier')
    .option('--custom-tag [custom-tag]', 'tag grid result with custom tag')
    .option(
        '-slb --skip-load-balancer',
        'skipping automatic allocation (AppiumLB) for mobile runs on mobile gird that support it',
        false,
    )
    .option('--no-lock-device', 'skipping locking device internal use for TDC grid')
    .option('--os-version [os-version]', 'The operating system version number')
    .option('--app-id [app-id]', 'The application id from app library on Testim Editor App')
    .option('--appium-log-level [appium-log-level]', 'Level of logging verbosity')
    .option('--set-no-reset [set-no-reset]', 'set noReset appium capability, by default set to false', false)
    .option(
        '-fr --full-reset [full-reset]',
        'set fullReset appium capability to true to reinstall application on session start and session end',
    )
    .option('--duration [duration]', 'The duration of the test run in minutes', Number)

    .option('-str --suppress-tms-reporting [suppress-tms-reporting]', 'disable test management reporting', false)
    .option('-tsr --tms-suppress-reporting [tms-suppress-reporting]', 'disable test management reporting', false)
    .option('-tid --tms-run-id [tms-run-id]', 'update existing result in test management', '')
    .option(
        '-tff --tms-field-file [tms-field-file.json]',
        'pass field file location to add custom result field to the tms result report',
        '',
    )

    .option('--disable-file-cache', 'disable internal CLI file caching')
    .option('--clear-file-cache', 'clear internal CLI file cache')
    .option('--file-cache-location [directory]', ' internal CLI file caching location')

    // Timeout
    .option(
        '--test-start-timeout [test-start-timeout]',
        'The time to wait for a test to start after getting a browser session',
        Number,
        Number(env.TESTIM_TEST_START_TIMEOUT) || 2 * 60 * 1000,
    )
    .option('--timeout [test-timeout]', 'Test run timeout in milliseconds', Number)
    .option('--browser-timeout [open-browser-timeout]', 'Get browser from grid timeout in milliseconds', Number)
    .option('--new-browser-wait-timeout [max-wait-to-browser]', 'Maximum get browser wait in minutes', Number)

    // New Timeouts
    .option(
        '--get-browser-timeout [get-browser-timeout]',
        "Timeout for a single attempt to get browser from the grid configured in the project's plan",
        Number,
    ) // getBrowserTimeout
    .option(
        '--get-browser-retries [get-browser-retries]',
        "Number of attempts to get browser from the grid configured in the project's plan",
        Number,
    ) // getBrowserRetries
    .option(
        '--get-session-timeout [get-session-timeout]',
        'Timeout for "/session" request to the selenium server',
        Number,
        ms('90s'),
    ) // getSessionTimeout
    .option(
        '--get-session-retries [get-session-retries]',
        'Retries for "/session" request to the selenium server',
        Number,
        3,
    ) // getSessionRetries
    .option(
        '--driver-request-timeout [driver-request-timeout]',
        'Timeout for any WebDriver request to the grid server',
        Number,
        ms('90s'),
    ) // driverRequestTimeout
    .option(
        '--driver-request-retries [driver-request-retries]',
        'Retries for any WebDriver request to the grid server',
        Number,
        3,
    ) // driverRequestRetries

    // Local run options
    .option('--local-tma-url [local-tma-url]', 'use a local TMA instance for appium session')
    .option(
        '--use-local-chrome-driver [use-local-chrome-driver]',
        'use a local ChromeDriver instance instead of a selenium grid',
    )
    .option('--chrome-binary-location [chrome-binary-location]', 'Chrome binary location')

    // Run chrome ext mode locally using chrome launcher
    .option('--use-chrome-launcher', 'use a local Chrome installation without selenium')

    // Mock network
    .option('-mnh --mock-network-har', 'use the HAR file configured in the Testim editor to mock network traffic')
    .option(
        '-mnp --mock-network-pattern [local file location path]',
        'use a JSON rule file to mock network traffic (Rule file schema: https://help.testim.io/page/mocking-network-traffic)',
    )
    .option(
        '-omf --override-mapping-file [local file location path]',
        'pass map file location to override mock network (see schema: https://help.testim.io/page/mocking-network-traffic)',
    )
    .option('-dmn --disable-mock-network', 'Disable mock mode for the entire CLI run')
    .option('--run-quarantined-tests', 'Run quarantine tests')

    // Code coverage
    .option('--collect-code-coverage', 'collect code coverage for all js files under base url')
    .option(
        '--code-coverage-url-filter [url pattern]',
        'collect code coverage for all js files matching url filter (url including asterisk)',
    )
    .option('--code-coverage-report-path [path]', 'where to save coverage report (default ./coverage)')
    .option('--code-coverage-source-map-path [path]', 'path of source code')
    .option(
        '--code-coverage-reporter [reporter]',
        `set code coverage reporter (default html and text), options: ${CODE_COVERAGE_REPORTER_OPTIONS.join('/')}`,
        collect,
        [],
    )
    .option('--code-coverage-include [pattern]', 'set selecting files for coverage (default src/**)', collect, [])

    // SauceLab
    .option('--sauce-user [sauce-lab-user]', 'user to connect to sauce labs')
    .option('--sauce-key [sauce-lab-key]', 'key to use when connecting to sauce labs')
    .option(
        '--sauce-options [sauce-options]',
        'DEPRECATED: use --custom-capabilities-name or --custom-capabilities-file instead. JSON file of browser and os options for sauce',
    )

    // Browserstack
    .option('--browserstack-user [browserstack-user]', 'user to connect to browserStack')
    .option('--browserstack-key [browserstack-key]', 'key to use when connecting to browserStack')
    .option(
        '--browserstack-options [browserstack-options]',
        'DEPRECATED: use --custom-capabilities-name or --custom-capabilities-file instead. JSON file of browser and os options for browserStack',
    )

    // Perfecto
    .option('--perfecto-token [perfecto-token]', 'security token to use when connecting to perfecto')
    .option(
        '--perfecto-options [perfecto-options]',
        'DEPRECATED: use --custom-capabilities-name or --custom-capabilities-file instead. JSON file of browser and os options for perfecto',
    )

    // Experitest
    .option('--experitest-token [experitest-token]', 'security token to use when connecting to experitest')

    // TestObject
    .option('--testobject-key [testobject-key]', 'api key to use when connecting to testobject')
    .option('--testobject-options [testobject-options]', 'json file of options for testobject')

    // SeaLights
    .option('--sealights-lab-id [sealights-lab-id]', 'SeaLight Lab ID')
    .option('--sealights-build-session-id [sealights-build-session-id]', 'SeaLight Session Build ID')
    .option('--sealights-test-stage [sealights-test-stage]', 'SeaLight Test Stage Name')

    // Extension
    .option('--ext [extension src path]', "use extension from path (default it '/..')")
    .option('--extension-path [path to extension archived file]', 'override the used extension')

    //Customer Extension
    .option(
        '--install-custom-extension [chrome extension zipped file url or local path]',
        'chrome extension to be installed in the browser',
    )

    // Player
    .option('--player-path [path to player file]')
    .option('--player-require-path [path to unminified player - development only]')

    // Node Inspect as a flag
    .option('--inspect [port]', 'Opens node inspector at given port', Number)

    // Tunnel
    .option('--tunneld', 'run a tunnel daemon only')
    .option('--tunnel [tunnel]', 'enable tunnel')
    .option('--tunnel-routes [routes]', 'tunnel routes for cloudflare tunnels')
    .option('--tunnel-port [tunnel-port]', 'tunnel port address')
    .option('--tunnel-host-header [tunnel-host-header]', 'tunnel host header')
    .option('--tunnel-use-http-address [tunnel-use-http-address]', 'use http:// address instead of https://', false)
    .option('--external-lambdatest-tunnel-id [tunnel-id]', 'use existing lambdatest tunnel ID')
    .option('--external-lambdatest-use-wss', 'use wss instead of ssh for LT', false)
    .option('--external-lambdatest-disable-automation-tunneling', "don't tunnel Testim calls in LT tunnel", true)
    .option('--external-lambdatest-mitm', 'Turn on LT Man in the middle', false)
    .option('--external-lambdatest-NTLM-tunnel-username [tunnel-NTLM-username]', 'Tunnel NTLM username')
    .option('--external-lambdatest-NTLM-tunnel-password [tunnel-NTLM-password]', 'Tunnel NTLM password')

    .option(
        '--w3c-capabilities [enable-w3c-caps-mode]',
        'enable/disable w3c capabilities format (default enable)',
        JSON.parse as any,
        true,
    )
    .option(
        '--old-capabilities [enable-old-caps-mode]',
        'enable/disable old capabilities format (default enable)',
        JSON.parse as any,
        true,
    )
    .option('--disable-sockets', 'Disable CLI sockets', false)

    // Remote run options
    .option('--executionId [execution-id]', '', '')
    .option('--remoteRunId [remote-run-id]', '', '')
    .option('--schedulerId [scheduler-id]', '', '')
    .option('--source [source]', '', 'cli')
    .option('--resultId [result-id]', '', '')

    // Agent mode
    .option('--connect, --agent [enable-agent-mode]', 'enable Testim CLI agent mode', false)
    .option('--start [enable-start]', 'Connect to testim and open the editor in a standalone browser', false)
    .option(
        '--download-browser',
        'when used with the start option, downloads a fixed version to run Testim editor in',
        false,
    )
    .option('--agent-port [agent-port]', 'set agent port', Number, 42543)
    .option('--agent-bind [agent-host-bind]', 'set agent host bind', '127.0.0.1')

    .option('--chrome-extra-prefs [chrome-extra-prefs]', 'add extra chrome preferences', '')
    .option('--chrome-extra-args [chrome-extra-args]', "add extra chrome arguments separated by ','", '')
    .option('--chrome-block-location [chrome-block-location]', 'block chrome geolocation', false)
    .option('--chrome-user-data-dir [chrome-user-data-dir]', 'use custom chrome user date dir', false)

    .option(
        '--disable-cookies-same-site-none-requires-secure [disable-same-site-none-requires-secure]',
        'Disable cookies without SameSite must be secure',
        false,
    )

    .option(
        '--selenium-caps-file [selenium-caps-file.json]',
        'DEPRECATED: use --custom-capabilities-name or --custom-capabilities-file instead. JSON file to merge into Testim selenium desired capabilities',
    )

    .option('--version [version]', 'print the current version of the Testim CLI and exit', false)
    .option('--monitor-performance', 'collect test playback performance data')

    .option('--high-speed', 'DEPRECATED: use --turbo-mode instead') // When removing, remove from the program.help output filter
    .option(
        '--turbo-mode',
        'run in turbo mode (providing that tests are configured with browsers that use extension mode [chrome, edge chromium])',
    )
    .option('--lightweight-mode [settings]', 'run lightweight mode')
    .option('--create-prefeched-data [location]', 'prefetch data into local cache file')
    .option(
        '--use-prefeched-data [location]',
        'use prefetched data from local cache file, and force using only cached data',
    )
    .option('--save-rca-locally [path]', 'save root cause analysis assets locally')

    .option(
        '--exit-code-ignore-failing-tests',
        'return exit code of zero when tests fail. non zero exit code will mean a real error occurred',
    )

    .option(
        '--intersect-with-label [label]',
        "Out of the execution's test list, run only those tests that have the specified label",
        collect,
        [],
    )
    .option(
        '--label-intersection-operand [operand]',
        'Run only tests that match either all the labels with the "AND" operand, or at least one of the labels with the "OR" operand',
        createDuplicateChecker('--label-intersection-operand'),
    )
    .option(
        '--intersect-with-suite [suiteName]',
        "Out of the execution's test list, run only those tests that are included in the specified suite (by suite name)",
        collect,
        [],
    )
    .option(
        '--intersect-with-suite-id [suiteId]',
        "Out of the execution's test list, run only those tests that are included in the specified suite (by suite ID)",
        collect,
        [],
    )
    .option(
        '--apply-intersections-to-before-and-after-sections [boolean]',
        "For a test plan's before/after tests, run tests that intersect with specified intersection flags",
        undefined,
    )

    .option('--install-lazy-deps [install-lazy-deps]', 'Install all lazy dependencies from package.json', false)
    .option(
        '--bypass-node-version-check [bypass-node-version-check]',
        'Allow running with incompatible Node.js versions',
        false,
    )
    .option('--print-grids', 'Print available grids and exit', false)
    .option('--print-final-caps', 'Print the final calculated capabilities', false)
    .option(
        '--headless-extension-download-path [headless-extension-download-path]',
        'Path to download headless extension',
        '',
    )

    // Custom capabilities
    .option('--custom-capabilities-name <name>', 'name of pre-created custom capabilities item')
    .option('--custom-capabilities-file <path>', 'path to a JSON file with capabilities settings')

    .parse(argv);

export const program = cli.opts();

const setHostAndPortForSauceLab = () => {
    if (program.grid || program.gridId) {
        return;
    }
    if (!program.host) {
        program.host = 'ondemand.saucelabs.com';
    }
    if (!program.port) {
        program.port = 80;
    }
};

const setHostAndPortForBrowserStack = () => {
    if (program.grid || program.gridId) {
        return;
    }
    if (!program.host) {
        program.host = 'hub-cloud.browserstack.com';
    }
    if (!program.port) {
        program.port = 80;
    }
};

const injectSecretsProxy = (codeFilePath: string) => {
    const isSecretsEnabled = featureFlagsService.flags.secrets.isEnabled({ projectId: program.project });
    if (!isSecretsEnabled) {
        return;
    }
    const code = fs.readFileSync(codeFilePath);
    if (!secretsUtils?.containsSecrets(code.toString())) {
        return;
    }
    secretsUtils.injectProxySecretsStoreToGlobal();
};

export const process = async (): Promise<Options> => {
    if (program.inspect) {
        const inspector = require('node:inspector');
        inspector.open(program.inspect);
    }

    let userParamsData = {};
    let chromeExtraPrefs = {};
    const chromeExtraArgs = [];
    let seleniumCapsFileContent = {};
    let customCapabilities: ICapabilities | undefined;

    if (!argv.slice(2).length) {
        printUsage();
        throw new NoArgsError();
    }

    if (program.version) {
        const message = 'Testim CLI Version: ';
        if (env.npm_package_version) {
            console.log(message, env.npm_package_version);
            exit(0);
        }
        const version = utils.getRunnerVersion();
        if (version) {
            console.log(message, version);
            exit(0);
        }
        console.log('Could not find version, please check the package.json manually');
        exit(0);
    }

    if (program.installLazyDeps) {
        return { installLazyDepsMode: true };
    }

    if (program.disableFileCache) {
        localRunnerCache.disable();
    }

    if (program.clearFileCache) {
        localRunnerCache.clear();
    }

    const cacheLocationProvided = program.fileCacheLocation || program.usePrefechedData || program.createPrefechedData;

    if (cacheLocationProvided) {
        const location = path.resolve(cacheLocationProvided);
        localRunnerCache.setCacheLocation(location);
    }

    if (program.usePrefechedData) {
        localRunnerCache.disableCacheMiss();
    }

    if (program.playerRequirePath) {
        const fullPlayerPath = path.resolve(program.playerRequirePath);
        console.log('Using Local Clickim for Player Require Path =', fullPlayerPath);
        const projectFile = path.join(fullPlayerPath, 'tsconfig.node.json');

        // [NOTE] playerRequirePath is a dev flag
        // eslint-disable-next-line import/no-extraneous-dependencies
        const tsnodeApi = require('ts-node');
        const tsNodeRegisterInstance = tsnodeApi.register({
            project: projectFile,
            ignore: [
                // restore default value
                /node_modules/,
                // based on that code:
                // https://github.com/TypeStrong/ts-node/blob/c1ae9a069a824368c9aaf89f4454b131af44a92f/src/index.ts#L368
                // it will not touch this project files, but will work on clickim code
                new RegExp(`^${_.escapeRegExp(path.relative(cwd(), __dirname))}`),
            ],
            transpileOnly: true,
        });

        // [NOTE] playerRequirePath is a dev flag
        // eslint-disable-next-line import/no-extraneous-dependencies
        const tsConfigPathsApi = require('tsconfig-paths');
        tsConfigPathsApi.register({
            paths: tsNodeRegisterInstance.config.options.paths,
            baseUrl: tsNodeRegisterInstance.config.options.baseUrl,
        });

        (
            require('./commons/getSessionPlayerRequire') as typeof import('./commons/getSessionPlayerRequire')
        ).options.playerPath = fullPlayerPath;
        const Module = require('node:module');
        const originalRequire = Module.prototype.require;
        Module.prototype.require = function requireThatOverridesSessionPlayer(id: string) {
            if (id === 'rox-alias') {
                return originalRequire.call(this, 'rox-node');
            }
            if (id === 'canvas') {
                return {};
            }
            return originalRequire.apply(this, arguments);
        };
    }

    if (program.caFile) {
        global.caFileContent = fs.readFileSync(program.caFile);
    }

    if (program.proxy) {
        global.proxyUri = program.proxy;
        // used by Clickim internally and by the runner, included here lazily to not make things slower for the
        // non-proxy case
        global.SuperagentProxy = require('superagent-proxy');
        global.ProxyAgent = require('proxy-agent').ProxyAgent;
        global.HttpsProxyAgent = require('https-proxy-agent').HttpsProxyAgent;
    }

    if (program.proxyForGrid && !program.proxy) {
        throw new ArgError('missing --proxy option');
    }

    if (runOptionsAgentFlow.isAgentFlow(program)) {
        return runOptionsAgentFlow.runAgentFlow(program);
    }

    // merge options from file
    try {
        let options = {} as object;
        if (program.configFile) {
            injectSecretsProxy(program.configFile);
            // eslint-disable-next-line import/no-dynamic-require
            options = require(path.join(cwd(), program.configFile)).config;
        } else if (program.optionsFile) {
            // eslint-disable-next-line import/no-dynamic-require
            options = require(path.join(cwd(), program.optionsFile));
        }

        // technically would work on anything, but no reason to add a tick for nothing.
        if (options && typeof (options as PromiseLike<any>).then === 'function') {
            // eslint-disable-next-line @typescript-eslint/await-thenable
            options = await options;
        }

        Object.keys(options).forEach((prop) => {
            const safePropName = camelizeHyphenValues(prop);
            program[safePropName] = mergeValues(program[safePropName], options[prop as keyof typeof options]);
        });
    } catch (err) {
        err.message = `Unable to read options file: ${err.message}`;
        throw err;
    }

    if (program.tunneld) {
        return {
            tunnel: true,
            tunnelPort: program.tunnelPort,
            tunnelRoutes: program.tunnelRoutes,
            tunnelRoutesOutput: program.tunnelRoutesOutput,
            tunnelHostHeader: program.tunnelHostHeader,
            tunnelRegion: program.tunnelRegion,
            tunnelDiagnostics: program.tunnelDiagnostics,
            tunnelUseHttpAddress: program.tunnelUseHttpAddress,
            tunnelOnlyMode: true,
            token: program.token,
            project: program.project,
        };
    }

    const isTestConfigSpecified = program.testConfig?.length || program.testConfigId?.length;
    const isTestPlanSpecified = program.testPlan?.length || program.testPlanId?.length;
    const isSuiteSpecified = program.suite?.length || program.suiteId?.length;

    if (program.customCapabilitiesFile && program.customCapabilitiesName) {
        throw new ArgError('You can either use --custom-capabilities-name or --custom-capabilities-file');
    }

    if (
        (program.customCapabilitiesFile || program.customCapabilitiesName) &&
        (program.browserstackOptions || program.sauceOptions || program.seleniumCapsFile || program.perfectoOptions)
    ) {
        throw new ArgError(
            "Custom capabilities parameters can't be used together with --browserstack-options, --sauce-options, --selenium-caps-file or --perfecto-options parameters",
        );
    }

    if (program.customCapabilitiesFile) {
        try {
            // eslint-disable-next-line import/no-dynamic-require
            customCapabilities = require(path.join(cwd(), program.customCapabilitiesFile)) as ICapabilities;
            checkForbiddenKeys(customCapabilities);
        } catch (err) {
            if (err instanceof ArgError) {
                throw err;
            }

            throw new ArgError(`Failed to parse custom capabilities file: ${err.message}`);
        }
    }

    if (program.seleniumCapsFile) {
        printDeprecationWarning('--selenium-caps-file', '--custom-capabilities-name or --custom-capabilities-file');
        try {
            // eslint-disable-next-line import/no-dynamic-require
            seleniumCapsFileContent = require(path.join(cwd(), program.seleniumCapsFile));
        } catch (err) {
            throw new ArgError(`Failed to parse selenium caps file file error: ${err.message}`);
        }
    }

    if (program.reporters?.includes('junit') && !program.reportFile) {
        console.log('Warning: please define --report-file option for JUnit reporter');
    }

    if (!program.tunnel && program.externalLambdatestTunnelId) {
        throw new ArgError('missing --tunnel parameter');
    }

    if (!program.tunnel && program.externalLambdatestUseWss) {
        throw new ArgError('missing --tunnel parameter');
    }

    if (
        !program.tunnel &&
        [program.tunnelPort, program.tunnelHostHeader, program.tunnelRegion, program.tunnelDiagnostics].some(Boolean)
    ) {
        throw new ArgError('missing --tunnel parameter');
    }
    if (program.chromeExtraPrefs) {
        try {
            // eslint-disable-next-line import/no-dynamic-require
            chromeExtraPrefs = require(path.join(cwd(), program.chromeExtraPrefs));
        } catch (err) {
            throw new ArgError(`Failed to read/open chrome extra prefs file error: ${err.message}`);
        }
    }

    if (program.chromeExtraArgs) {
        const args = program.chromeExtraArgs.split(',');
        for (const arg of args) {
            const [argName] = arg.split('=');
            if (allowedChromeFlags.includes(argName) || program.useLocalChromeDriver || program.useChromeLauncher) {
                chromeExtraArgs.push(arg);
                if (argName === 'proxy-server') {
                    chromeExtraArgs.push('proxy-bypass-list=*.testim.io;*.betterstackdata.com;*.rollout.io');
                }
            } else {
                console.warn(
                    `Ignoring an unsupported chrome arg (${argName}), allowed values: ${JSON.stringify(allowedChromeFlags)}`,
                );
            }
        }
    }

    if (program.paramsFile) {
        try {
            if ((program.paramsFile as string).endsWith('js')) {
                injectSecretsProxy(program.paramsFile);
            }
            // eslint-disable-next-line import/no-dynamic-require
            userParamsData = { ...userParamsData, ...require(path.join(cwd(), program.paramsFile)) };
        } catch (err) {
            throw new ArgError(`Failed to read/open params file error: ${err.message}`);
        }
    }

    if (program.params) {
        try {
            userParamsData = { ...userParamsData, ...JSON.parse(program.params) };
        } catch (err) {
            throw new ArgError(`Failed to parse params string error: ${err.message}`);
        }
    }

    // SauceLabs Options
    if ((program.sauceUser && !program.sauceKey) || (!program.sauceUser && program.sauceKey)) {
        throw new ArgError('missing --sauce-user <sauce-user> or --sauce-key <sauce-key>');
    }

    if (program.sauceUser && program.sauceKey) {
        setHostAndPortForSauceLab();
        program.saucelabs = {};

        program.saucelabs.username = program.sauceUser;
        program.saucelabs.accessKey = program.sauceKey;
    }

    if (program.sauceOptions) {
        printDeprecationWarning('--sauce-options', '--custom-capabilities-name or --custom-capabilities-file');
        try {
            // eslint-disable-next-line import/no-dynamic-require
            const sOptions = require(path.join(cwd(), program.sauceOptions));
            const isMobile = sOptions.platformName && ['ios', 'android'].includes(sOptions.platformName.toLowerCase());
            if (sOptions.browserName) {
                const browserName = sOptions.browserName.toLowerCase();
                switch (browserName) {
                    case 'microsoftedge':
                        program.browser = 'edge';
                        break;
                    default:
                        program.browser = browserName;
                }
            }

            if (program.browser === 'edge' && parseFloat(sOptions.version) >= EDGE_CHROMIUM_MIN_VERSION) {
                program.browser = 'edge-chromium';
            }

            const isBadVersion = parseFloat(sOptions.version) < 50 && !['dev', 'beta'].includes(sOptions.version);
            if (!isMobile && program.browser === 'chrome' && isBadVersion) {
                throw new ArgError('The minimum chrome supported version is 50.0');
            }

            program.saucelabs = { ...program.saucelabs, ...sOptions };
        } catch (err) {
            throw new ArgError(`Failed to parse saucelabs options file error: ${err.message}`);
        }
    }

    // BrowserStack options
    if (
        (program.browserstackUser && !program.browserstackKey) ||
        (!program.browserstackUser && program.browserstackKey)
    ) {
        throw new ArgError('missing --browserstack-user <browserstack-user> or --browserstack-key <browserstack-key>');
    }
    if (program.browserstackUser && program.browserstackKey) {
        setHostAndPortForBrowserStack();
        program.browserstack = {};

        program.browserstack['browserstack.user'] = program.browserstackUser;
        program.browserstack['browserstack.key'] = program.browserstackKey;
    }

    if (program.browserstackOptions) {
        printDeprecationWarning('--browserstack-options', '--custom-capabilities-name or --custom-capabilities-file');
        try {
            // eslint-disable-next-line import/no-dynamic-require
            const bsOptions = require(path.join(cwd(), program.browserstackOptions));
            const isMobile = bsOptions.platform && ['mac', 'android'].includes(bsOptions.platform.toLowerCase());
            if (bsOptions.browserName) {
                program.browser = bsOptions.browserName.toLowerCase();
            }

            if (program.browser === 'edge' && parseFloat(bsOptions.browser_version) >= EDGE_CHROMIUM_MIN_VERSION) {
                program.browser = 'edge-chromium';
            }

            if (!isMobile && parseFloat(bsOptions.browser_version) < 50 && program.browser === 'chrome') {
                throw new ArgError('The minimum chrome supported version is 50.0');
            }

            program.browserstack = { ...program.browserstack, ...bsOptions };
        } catch (err) {
            throw new ArgError(`Failed to parse browserstack options file error: ${err.message}`);
        }
    }

    program.perfecto = {};

    if (program.perfectoToken) {
        program.perfecto.securityToken = program.perfectoToken;
    }

    if (program.perfectoOptions) {
        printDeprecationWarning('--perfecto-options', '--custom-capabilities-name or --custom-capabilities-file');
        try {
            // eslint-disable-next-line import/no-dynamic-require
            const perfectoOptions = require(path.join(cwd(), program.perfectoOptions));
            const DEFAULTS = { location: 'US East', securityToken: program.perfectoToken };
            program.perfecto = { ...DEFAULTS, ...perfectoOptions };
        } catch (err) {
            throw new ArgError(`Failed to parse perfecto options file error: ${err.message}`);
        }
    }

    program.testobjectSauce = {};

    if (program.testobjectKey) {
        program.testobjectSauce.testobjectApiKey = program.testobjectKey;
    }

    if (program.testobjectOptions) {
        try {
            // eslint-disable-next-line import/no-dynamic-require
            const testobjectOptions = require(path.join(cwd(), program.testobjectOptions));
            const DEFAULTS = { testobjectApiKey: program.testobjectKey };
            program.testobjectSauce = { ...DEFAULTS, ...testobjectOptions };
        } catch (err) {
            throw new ArgError(`Failed to parse test object options file error: ${err.message}`);
        }
    }

    const testOptimization: TestOptimization = {
        seaLights: {
            buildSessionId: program.sealightsBuildSessionId,
            labId: program.sealightsLabId,
            testStage: program.sealightsTestStage,
        },
    };
    program.testOptimization = testOptimization;

    if (!program.project) {
        const credentialsManager = await import('./credentialsManager');
        const projectId = await credentialsManager.getProjectId();

        if (projectId) {
            program.project = projectId;
        } else {
            throw new ArgError(
                'missing project-id info, either --login to provide new credentials or use --project <project-id>',
            );
        }
    }
    setRegionConfig(program.project);

    const forceTurboModeIfNeeded = featureFlagsService.flags.forceTurboModeIfNeeded.isEnabled({
        projectId: program.project,
    });
    if (forceTurboModeIfNeeded) {
        program.turboMode = true;
    }

    if (program.testConfig) {
        // convert single test config inputs to array (e.g. from configFile)
        program.testConfig = [program.testConfig].flat();
    }

    if (program.testConfigId) {
        // convert single test config inputs to array (e.g. from configFile)
        program.testConfigId = [program.testConfigId].flat();
    }

    program.retries = !program.retries || typeof program.retries === 'boolean' ? 1 : Number(program.retries) + 1;
    program.browserTimeout =
        !program.browserTimeout || typeof program.browserTimeout === 'boolean' ? 60 * 1000 : program.browserTimeout;
    program.newBrowserWaitTimeout =
        !program.newBrowserWaitTimeout || typeof program.newBrowserWaitTimeout === 'boolean'
            ? 10 * 60 * 1000
            : program.newBrowserWaitTimeout * 60 * 1000;

    if (!program.getBrowserTimeout) {
        program.getBrowserTimeout = program.browserTimeout;
    }
    if (!program.getBrowserRetries) {
        program.getBrowserRetries = Math.round(program.newBrowserWaitTimeout / program.browserTimeout);
    }
    program.getSessionTimeout = Math.max(program.browserTimeout, program.getSessionTimeout);
    program.driverRequestTimeout = Math.max(program.browserTimeout, program.driverRequestTimeout);

    const timeoutWasGiven = Boolean(program.timeout);
    program.timeout = !program.timeout || typeof program.timeout === 'boolean' ? 10 * 60 * 1000 : program.timeout;
    program.beforeParallel =
        !program.beforeParallel || typeof program.beforeParallel === 'boolean' ? 1 : Number(program.beforeParallel);
    program.parallel = !program.parallel || typeof program.parallel === 'boolean' ? 1 : Number(program.parallel);
    program.afterParallel =
        !program.afterParallel || typeof program.afterParallel === 'boolean' ? 1 : Number(program.afterParallel);
    program.tunnelPort = !program.tunnelPort || typeof program.tunnelPort === 'boolean' ? '80' : program.tunnelPort;

    program.port &&= Number(program.port);

    if (!program.token) {
        const credentialsManager = await import('./credentialsManager');
        const credentialToken = await credentialsManager.getToken();

        if (credentialToken) {
            program.token = credentialToken;
        } else {
            throw new ArgError(
                'missing Testim Access Token, either --login to provide new credentials or use --token <testim-access-token>, contact info@testim.io if you need a new one.',
            );
        }
    }

    const optionIsNotAValidNumber = (option: any) => option <= 0 || !Number.isInteger(option);

    if (optionIsNotAValidNumber(program.retries)) {
        throw new ArgError(
            'test failure retry count could not be a negative number or string, --retries <max_num_of_retries>',
        );
    }

    if (program.retries > 21) {
        throw new ArgError(
            'Max number of retries exceeded. Number cannot be greater than 20, --retries <max_num_of_retries>',
        );
    }

    if (optionIsNotAValidNumber(program.browserTimeout)) {
        throw new ArgError(
            'get browser timeout could not be a negative number, --browser-timeout <get-browser-timeout>',
        );
    }

    if (optionIsNotAValidNumber(program.newBrowserWaitTimeout)) {
        throw new ArgError(
            'max new browser wait timeout could not be a negative number, --new-browser-wait-timeout <max-wait-to-browser>',
        );
    }

    if (optionIsNotAValidNumber(program.timeout)) {
        throw new ArgError('test run timeout could not be a negative number, --timeout <run-timeout>');
    }

    if (optionIsNotAValidNumber(program.beforeParallel)) {
        throw new ArgError(
            'before-parallel could not be a negative number or not number, --before-parallel <number-of-tests>',
        );
    }

    if (optionIsNotAValidNumber(program.parallel)) {
        throw new ArgError('parallel could not be a negative number or not number, --parallel <number-of-tests>');
    }

    if (optionIsNotAValidNumber(program.afterParallel)) {
        throw new ArgError(
            'after-parallel could not be a negative number or not number, --after-parallel <number-of-tests>',
        );
    }

    if (program.mode && ![CLI_MODE.EXTENSION, CLI_MODE.SELENIUM, CLI_MODE.APPIUM].includes(program.mode)) {
        throw new ArgError(`runner mode <${program.mode}> is not supported`);
    }

    if (program.browser && ![...extensionBrowsers, ...seleniumBrowsers].includes(program.browser)) {
        throw new ArgError(`browser ${program.browser} is not supported`);
    }

    if (program.mode !== CLI_MODE.SELENIUM && program.disableNativeEvents) {
        throw new ArgError('--disable-native-events is only applicable in selenium mode');
    }

    if (!program.appiumLogLevel) {
        program.appiumLogLevel = 'silent';
    }

    if (!LOG_LEVEL_OPTIONS.has(program.appiumLogLevel)) {
        throw new ArgError(`runner appium-log-level <${program.appiumLogLevel}> is not supported`);
    }

    if (program.testPlan && program.testPlan.length === 0 && program.testPlanId && program.testPlanId.length === 0) {
        if (
            typeof program.host !== 'string' &&
            typeof program.grid !== 'string' &&
            typeof program.gridId !== 'string' &&
            !program.localTmaUrl &&
            !program.useLocalChromeDriver &&
            !program.useChromeLauncher &&
            !program.createPrefechedData &&
            !program.printGrids
        ) {
            throw new ArgError(
                'missing remote grid address parameter, specify --host <host-name-or-ip> or --grid <grid-name> or --grid-id <grid-id>',
            );
        }
    } else if (
        program.testId.length ||
        program.label.length ||
        program.name.length ||
        isTestConfigSpecified ||
        program.browser ||
        isSuiteSpecified ||
        program.localTmaUrl ||
        program.useLocalChromeDriver ||
        program.useChromeLauncher
    ) {
        throw new ArgError(
            'cannot set --testId, --label, --name, --browser, --test-config, --test-config-id, --local-tma-url, --use-local-chrome-driver --use-chrome-launcher or --suite with --test-plan option',
        );
    }

    if (!isTestPlanSpecified && (program.beforeParallel !== 1 || program.afterParallel !== 1)) {
        throw new ArgError('cannot set --before-parallel or --after-parallel without --test-plan option');
    }

    const numberOfDefinedHosts = [program.host, program.grid, program.gridId].filter(Boolean).length;
    if (numberOfDefinedHosts > 1) {
        throw new ArgError('please define exactly one of --grid or --grid-id or --host');
    }

    if (program.host?.includes('/')) {
        if (!/^(f|ht)tps?:\/\//i.test(program.host)) {
            program.host = `http://${program.host}`;
        }
        program.host = url.parse(program.host).hostname;
    }

    if (program.resultLabel.length) {
        program.resultLabel = (program.resultLabel as string[]).map((label) => label.trim()).filter(Boolean);
        const invalidLabels = (program.resultLabel as string[]).filter((label) => label.length >= 250).filter(Boolean);
        if (
            program.branch === 'auto-detect' &&
            program.resultLabel.includes('auto-detect-branch') &&
            utils.getEnvironmentGitBranch()
        ) {
            program.resultLabel.unshift(utils.getEnvironmentGitBranch());
        }
        if (invalidLabels.length) {
            throw new ArgError('A result label cannot exceed 250 characters');
        }
    }

    const playerUrl = runOptionsUtils.getPlayerUrl(program);

    if (!program.w3cCapabilities && !program.oldCapabilities) {
        throw new ArgError('cannot set --w3c-capabilities and --old-capabilities options as false');
    }
    program.protocol ||= program.port === 443 ? 'https' : 'http';
    if (!['http', 'https'].includes(program.protocol)) {
        throw new ArgError('invalid --protocol value, allow --protocol http or https');
    }

    if (program.rerunFailedByRunId && program.branch) {
        throw new ArgError(
            'It is not possible to use --branch with --rerun-failed-by-run-id. Tests will automatically run on the same branch that was used in the original run',
        );
    }

    if (
        program.rerunFailedByRunId &&
        (isSuiteSpecified ||
            program.name.length ||
            program.testId.length ||
            program.label.length ||
            isTestPlanSpecified)
    ) {
        throw new ArgError(
            'Re-running failed tests is not possible when suite (--suite),' +
                ' label (--label), plan (--test-plan), or other test flags (--test) are provided. Please remove these flags and try again',
        );
    }

    if (program.setRetention && !_.inRange(_.parseInt(program.setRetention), 1, 11)) {
        throw new ArgError(
            'Please provide the number of days that the test results will be retained for (--set-retention must be a whole number between 1 to 10)',
        );
    }
    program.setRetention &&= Number(program.setRetention);

    const mockNetworkDeprecationMsg = 'is no longer supported, please use --override-mapping-file';
    if (program.mockNetworkHar) {
        throw new ArgError(`--mock-network-har ${mockNetworkDeprecationMsg}`);
    }
    if (program.mockNetworkPattern) {
        throw new ArgError(`--mock-network-pattern ${mockNetworkDeprecationMsg}`);
    }

    if (program.disableMockNetwork && program.overrideMappingFile) {
        throw new ArgError('You can either use --disable-mock-network or --override-mapping-file');
    }

    if (!program.collectCodeCoverage && program.codeCoverageSourceMapPath) {
        throw new ArgError('cannot set --code-coverage-source-map-path without passing --collect-code-coverage');
    }

    if (!program.collectCodeCoverage && program.codeCoverageReporter.length) {
        throw new ArgError('cannot set --code-coverage-reporter without passing --collect-code-coverage');
    }

    if (!program.collectCodeCoverage && program.codeCoverageInclude.length) {
        throw new ArgError('cannot set --code-coverage-include without passing --collect-code-coverage');
    }

    if (
        program.collectCodeCoverage &&
        program.codeCoverageReporter &&
        _.difference(program.codeCoverageReporter, CODE_COVERAGE_REPORTER_OPTIONS).length
    ) {
        const diff = _.difference(program.codeCoverageReporter, CODE_COVERAGE_REPORTER_OPTIONS);
        throw new ArgError(`invalid --code-coverage-reporter parameters ${diff.join('/')}`);
    }

    program.codeCoverageReporter =
        program.codeCoverageReporter.length === 0 ? ['html', 'text'] : program.codeCoverageReporter;
    program.codeCoverageInclude = program.codeCoverageInclude.length === 0 ? ['src/**'] : program.codeCoverageInclude;

    const extensionOnlyOptions = {
        mockNetworkHar: '--mock-network-har',
        mockNetworkPattern: '--mock-network-pattern',
        overrideMappingFile: '--override-mapping-file',
        codeCoverageUrlFilter: '--code-coverage-url-filter',
        collectCodeCoverage: '--collect-code-coverage',
        disableMockNetwork: '--disable-mock-network',
        useChromeLauncher: '--use-chrome-launcher',
    };
    const usedExtensionOptions = Object.keys(extensionOnlyOptions).filter((key) => Boolean(program[key]));

    if ([CLI_MODE.APPIUM, CLI_MODE.SELENIUM].includes(program.mode) && usedExtensionOptions.length) {
        const multi = usedExtensionOptions.length > 1 ? 'are' : 'is';
        throw new ArgError(
            `${usedExtensionOptions.map((key) => extensionOnlyOptions[key as keyof typeof extensionOnlyOptions]).join(' and ')} ${multi} only applicable in extension mode`,
        );
    }

    if (program.useChromeLauncher || program.useLocalChromeDriver) {
        program.browser = 'chrome';
    }

    if (program.tmsFieldFile) {
        try {
            const fileContent = fs.readFileSync(program.tmsFieldFile);
            // @ts-expect-error JSON.parse handles buffers as well, the types are not smart enough :(
            program.tmsCustomFields = JSON.parse(fileContent);
        } catch (err) {
            throw new ArgError(`failed to parse field file error: ${err.message}`);
        }
    }

    if (
        program.applyIntersectionsToBeforeAndAfterSections &&
        !program.intersectWithLabel &&
        !program.intersectWithSuite &&
        !program.intersectWithSuiteId
    ) {
        throw new ArgError(
            '--apply-intersections-to-before-and-after-sections flag must be combined with at least one of the available --intersect-with flags',
        );
    }

    if (program.labelIntersectionOperand === true) {
        throw new ArgError(
            'The --label-intersection-operand flag requires a value. Please provide a value after the flag.',
        );
    }

    if (program.labelIntersectionOperand && !Object.values(OPERANDS).includes(program.labelIntersectionOperand)) {
        throw new ArgError(
            `The --label-intersection-operand flag must be one of the following values: ${Object.values(OPERANDS).join(', ')}`,
        );
    }

    if (program.labelIntersectionOperand && _.isEmpty(program.intersectWithLabel)) {
        throw new ArgError(
            'The --label-intersection-operand flag must be used together with the --intersect-with-label flag.',
        );
    }

    /** Handling deprecation of High speed mode (renamed to Turbo mode) */
    if (program.highSpeed) {
        printDeprecationWarning('--high-speed', '--turbo-mode');
        program.turboMode = true;
    }

    if (program.skipLoadBalancer && !program.deviceUdid) {
        throw new ArgError('It is not possible to skip load balancer without specifying deviceUdid');
    }

    if (program.deviceUdid && (program.deviceName || program.osVersion)) {
        throw new ArgError(
            'It is not possible to use --osVersion or --deviceName with --device-udid. device-udid is unique and cannot be combined with another flag',
        );
    }
    if (program.setNoReset && program.fullReset) {
        throw new ArgError(
            'It is not possible to set both --set-no-reset and --full-reset capabilities to true at the same time!',
        );
    }

    if (program.lightweightMode) {
        try {
            const DEFAULTS = {
                general: true,
                disableLabs: true,
                disableFeatureFlags: true,
                disableAssets: true,
                disablePixelValidation: true,
                disableResults: true,
                disableStepDelay: true,
                disableRemoteStep: true,
                assumePreloadedSharedSteps: true,
                disableVisibilityCheck: false,
                disableLocators: false,
                bypassSetup: false,
                disableAutoImprove: true,
                disableQuotaBlocking: true,
                onlyTestIdsNoSuite: true,
                uploadAssetsAndResultsOnFailure: true,
                preloadTests: true,
                disableProjectDefaults: true,
                type: 'lightweight',
            };

            const lightweightModeOptions =
                typeof program.lightweightMode === 'string' ? JSON.parse(program.lightweightMode) : {};
            program.lightweightMode = { ...DEFAULTS, ...lightweightModeOptions };
        } catch (err) {
            throw new ArgError(`failed to parse lightweightMode settings error: ${err.message}`);
        }
    } else if (program.turboMode && ![CLI_MODE.APPIUM, CLI_MODE.SELENIUM].includes(program.mode)) {
        program.lightweightMode = {
            general: true,
            disableLabs: false,
            disableFeatureFlags: false,
            disableAssets: true,
            disablePixelValidation: false,
            disableResults: true,
            disableStepDelay: true,
            disableRemoteStep: false,
            assumePreloadedSharedSteps: false,
            disableVisibilityCheck: false,
            disableLocators: false,
            bypassSetup: false,
            disableQuotaBlocking: false,
            disableAutoImprove: false,
            onlyTestIdsNoSuite: false,
            uploadAssetsAndResultsOnFailure: true,
            preloadTests: false,
            disableProjectDefaults: false,
            type: 'turboMode',
        };
    } else if (program.turboMode && [CLI_MODE.APPIUM, CLI_MODE.SELENIUM].includes(program.mode)) {
        console.warn(
            '\nWhen running the CLI in Selenium mode, the Turbo Mode option is ignored, as they are not compatible concurrently',
        );
    }

    if (typeof program.baseUrl === 'boolean') {
        throw new ArgError('base url cannot be used as a flag, and must contain a string value');
    }

    return {
        testId: [program.testId].flat(),
        name: [program.name as unknown as string | string[]].flat(),
        label: [program.label].flat(),
        suites: [program.suite].flat(),
        suiteIds: [program.suiteId].flat(),
        testPlan: [program.testPlan].flat(),
        testPlanIds: [program.testPlanId].flat(),
        reportFile: program.reportFile,
        urls: program.urls,
        reportFileClassname: program.overrideReportFileClassname,
        reporters: program.reporters,
        project: program.project,
        host: program.host,
        headless: program.headless,
        localTmaUrl: program.localTmaUrl,
        useLocalChromeDriver: program.useLocalChromeDriver,
        chromeBinaryLocation: program.chromeBinaryLocation,
        useChromeLauncher: program.useChromeLauncher,
        port: program.port,
        grid: program.grid,
        gridId: program.gridId,
        disableNativeEvents: program.disableNativeEvents,
        saucelabs: program.saucelabs,
        browserstack: program.browserstack,
        baseUrl: program.baseUrl,
        branch: (program.branch === 'auto-detect' ? utils.getEnvironmentGitBranch() : program.branch) || 'master',
        autoDetect: program.branch === 'auto-detect',
        token: program.token,
        userAccessKey: program.userAccessKey,
        userParamsData,
        mode: program.mode,
        browser: program.browser,
        beforeParallel: program.beforeParallel,
        parallel: program.parallel,
        afterParallel: program.afterParallel,
        canary: program.canary,
        rerunFailedByRunId: program.rerunFailedByRunId,
        disableGridCheck: program.disableGridCheck,
        disableTimeoutRetry: program.disableTimeoutRetry,
        resultLabels: program.resultLabel,
        path: program.path,
        protocol: program.protocol,
        perfecto: program.perfecto,
        experitestToken: program.experitestToken,
        testobjectSauce: program.testobjectSauce,
        testOptimization: program.testOptimization,
        gridUsername: program.gridUsername,
        gridPassword: program.gridPassword,
        overrideExecutionName: program.overrideExecutionName,

        tmsSuppressReporting: Boolean(program.suppressTmsReporting) || Boolean(program.tmsSuppressReporting),
        tmsRunId: program.tmsRunId,
        tmsCustomFields: program.tmsCustomFields,

        proxyForGrid: program.proxyForGrid,
        retentionDays: program.setRetention,
        passZeroTests: Boolean(program.passZeroTests),
        runQuarantinedTests: Boolean(program.runQuarantinedTests),
        //Mobile-Flags
        deviceName: program.deviceName,
        deviceUdid: program.deviceUdid,
        customTag: program.customTag,
        osVersion: program.osVersion,
        duration: program.duration,
        skipLoadBalancer: program.skipLoadBalancer,
        lockDevice: program.lockDevice,
        appId: program.appId,
        appiumLogLevel: program.appiumLogLevel,
        setNoReset: program.setNoReset,
        fullReset: program.fullReset,

        // Extension
        ext: program.ext,
        extensionPath: program.extensionPath,
        extensionLocation: [program.extensionPath].filter(Boolean),

        // Player
        playerLocation: program.playerPath || playerUrl,
        playerPath: program.playerPath,
        playerRequirePath: program.playerRequirePath,

        // Tunnel
        tunnel: program.tunnel,
        tunnelPort: program.tunnelPort,
        tunnelRoutes: program.tunnelRoutes,
        tunnelRoutesOutput: program.tunnelRoutesOutput,
        tunnelHostHeader: program.tunnelHostHeader,
        tunnelRegion: program.tunnelRegion,
        tunnelDiagnostics: program.tunnelDiagnostics,
        tunnelUseHttpAddress: program.tunnelUseHttpAddress,
        externalLambdatestTunnelId: program.externalLambdatestTunnelId,
        externalLambdatestNTLMTunnelUsername: program.externalLambdatestNTLMTunnelUsername,
        externalLambdatestNTLMTunnelPassword: program.externalLambdatestNTLMTunnelPassword,
        externalLambdatestUseWss: program.externalLambdatestUseWss || false,
        externalLambdatestDisableAutomationTunneling: Boolean(program.externalLambdatestDisableAutomationTunneling),
        externalLambdatestMitm: Boolean(program.externalLambdatestMitm),

        // Hooks
        beforeTest: program.beforeTest,
        afterTest: program.afterTest,
        beforeSuite: program.beforeSuite,
        afterSuite: program.afterSuite,

        // Timeout
        timeout: program.timeout,
        timeoutWasGiven,
        browserTimeout: program.browserTimeout,
        newBrowserWaitTimeout: program.newBrowserWaitTimeout,

        // New Timeouts
        getBrowserTimeout: program.getBrowserTimeout,
        getBrowserRetries: program.getBrowserRetries,
        getSessionTimeout: program.getSessionTimeout,
        getSessionRetries: program.getSessionRetries,
        driverRequestTimeout: program.driverRequestTimeout,
        driverRequestRetries: program.driverRequestRetries,

        testStartTimeout: program.testStartTimeout,

        testConfigNames: program.testConfig,
        testConfigIds: program.testConfigId,

        // Mock network
        overrideMappingFile: program.overrideMappingFile,
        disableMockNetwork: program.disableMockNetwork,

        // Code coverage
        codeCoverageUrlFilter: program.codeCoverageUrlFilter,
        collectCodeCoverage: program.collectCodeCoverage,
        codeCoverageReportPath: program.codeCoverageReportPath,
        codeCoverageSourceMapPath: program.codeCoverageSourceMapPath,
        codeCoverageReporter: program.codeCoverageReporter,
        codeCoverageInclude: program.codeCoverageInclude,

        // Remote run options
        executionId: program.executionId,
        remoteRunId: program.remoteRunId,
        schedulerId: program.schedulerId,
        source: program.source,
        resultId: program.resultId,

        //Customer Extension
        installCustomExtension: program.installCustomExtension,

        w3cCapabilities: program.w3cCapabilities,
        oldCapabilities: program.oldCapabilities,

        chromeBlockLocation: program.chromeBlockLocation,
        chromeUserDataDir: program.chromeUserDataDir,
        retries: program.retries,
        chromeExtraPrefs,
        chromeExtraArgs,

        disableCookiesSameSiteNoneRequiresSecure: program.disableCookiesSameSiteNoneRequiresSecure,

        seleniumCapsFileContent,
        shouldMonitorPerformance: program.monitorPerformance,

        user: program.user,

        lightweightMode: program.lightweightMode,
        createPrefechedData: program.createPrefechedData,

        saveRCALocally: program.saveRcaLocally,
        exitCodeIgnoreFailingTests: program.exitCodeIgnoreFailingTests,

        disableSockets: program.disableSockets,

        // intersections
        intersections: {
            labels: program.intersectWithLabel.length ? [program.intersectWithLabel].flat() : undefined,
            suiteNames: program.intersectWithSuite.length ? [program.intersectWithSuite].flat() : undefined,
            suiteIds: program.intersectWithSuiteId.length ? [program.intersectWithSuiteId].flat() : undefined,
            applyIntersectionsToBeforeAndAfterSections: program.applyIntersectionsToBeforeAndAfterSections,
            labelIntersectionOperand: program.labelIntersectionOperand,
        },

        // custom test data
        testDataFileName: program.testData,

        downloadBrowser: program.downloadBrowser,
        slotService: 'mongo',
        printGrids: program.printGrids,
        printFinalCaps: program.printFinalCaps,

        customCapabilities,
        customCapabilitiesName: program.customCapabilitiesName,
    };
};

export interface InstallLazyDepsOptions {
    installLazyDepsMode: true;
}

export interface LoginModeOptions {
    loginMode: true;
}

export interface AgentModeOptions {
    agentMode: true;
    project?: string;
    token?: string;
    agentPort: number;
    agentBind?: string;
    openEditor?: boolean;
    startTestimBrowser: boolean;
    ext?: string;
    extensionPath?: string;
    playerLocation: string;
    canary?: boolean;
    playerPath?: string;
    playerRequirePath?: string;
    downloadBrowser: boolean;
}

export interface TunnelDaemonOptions {
    tunnelOnlyMode: true;
    tunnelRoutes: string[];
    tunnelRoutesOutput: string;
    tunnelHostHeader?: string;
    tunnelRegion?: string;
}

export interface TunnelOptions extends TunnelDaemonOptions, ServerDetailsBasedOnOptions {
    tunnel?: boolean;
    tunnelUseHttpAddress?: boolean;
    token: string;
    project: string;
    tunnelDiagnostics?: boolean;
    tunnelPort?: number;
}

export interface SaucelabsOptions {
    username: string;
    accessKey: string;
    platform?: string;
    platformName?: string;
    browserVersion?: string;
    platformVersion?: string;
    deviceName?: string;
    version?: string;
    'sauce:options'?: {
        [key: string]: string;
    };
    browserName?: string;
}

export interface BrowserstackOptions {
    'browserstack.user': string;
    'browserstack.key': string;
    os_version?: string;
    platform?: string;
    platformVersion?: string;
    deviceName?: string;
    build?: string;
    project?: string;
    name?: string;
    browserName?: string;
}

type CodeCoverageReporter =
    | 'clover'
    | 'html'
    | 'json-summary'
    | 'json'
    | 'lcov'
    | 'lcovonly'
    | 'teamcity'
    | 'text-lcov'
    | 'text-summary'
    | 'text';

type AppiumLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent';

type Operand = (typeof OPERANDS)[keyof typeof OPERANDS];

export interface LightweightSettings {
    type: 'lightweight' | 'turboMode' | 'highSpeed';
    general: boolean;
    disableLabs: boolean;
    disableFeatureFlags: boolean;
    disableAssets: boolean;
    disablePixelValidation: boolean;
    disableResults: boolean;
    disableStepDelay: boolean;
    disableRemoteStep: boolean;
    assumePreloadedSharedSteps: boolean;
    disableVisibilityCheck: boolean;
    disableLocators: boolean;
    bypassSetup: boolean;
    disableAutoImprove: boolean;
    disableQuotaBlocking: boolean;
    onlyTestIdsNoSuite: boolean;
    uploadAssetsAndResultsOnFailure: boolean;
    preloadTests: boolean;
    disableProjectDefaults: boolean;
}
export interface ProjectData extends Pick<InitializedUserWithAuth['projectById'], 'type' | 'name' | 'defaults'> {
    projectId: string;
}
// Properties here are marked as optional but are always being added later since the options mutate (except for `sfdcCredential`)
interface ServerDetailsBasedOnOptions {
    company?: {
        ucid: string;
        companyId: string;
        name: string;
        planType: 'pro' | 'free' | 'trial';
        isPOC: boolean;
        isStartUp: boolean;
        activePlan: import('services/src/commons/mongo/types/DbCompanyProjects').Plan;
    };
    gridData?: SerializedGrid & { failedGetBrowserAttempts?: number };
    sfdcCredential?: string;
    editorUrl?: string;
    allGrids?: InitializedUserWithAuth['allGrids'];
    authData?: InitializedUserWithAuth['authData'];
    projectData?: ProjectData;
    userData?: UserData;
}
interface BeforeTestHook {
    (
        params: TestRunStatusTestResult & {
            exportsGlobal: { [key: string]: any };
            globalParameters: { [key: string]: any };
        },
        token: string,
    ): PromiseLike<{ [key: string]: any } | undefined>;
}
interface AfterTestHook {
    (
        params: TestRunStatusTestResult & {
            exportsGlobal: { [key: string]: any };
            globalParameters: { [key: string]: any };
        },
        token: string,
    ): PromiseLike<void>;
}

interface BeforeSuiteHook {
    (params: {
        projectId: string;
        executionId: string;
        testNames?: string[];
    }): PromiseLike<{ [key: string]: any } | undefined>;
}

interface AfterSuiteHook {
    (params: {
        exportsGlobal: { [key: string]: any };
        tests: any[];
        total: number;
        passed: number;
        skipped: number;
    }): PromiseLike<void>;
}

// TODO: consider typing better, based on the validations we do (example: must have one of grid/grid-id/host&port/test plan)
export interface RunnerOptions
    extends ServerDetailsBasedOnOptions,
        Partial<Omit<TunnelOptions, 'tunnelOnlyMode' | 'tunnel' | keyof ServerDetailsBasedOnOptions>> {
    token: string;
    project: string;
    user?: string;
    userAccessKey?: string;
    headless?: boolean;
    disableNativeEvents?: boolean;
    baseUrl?: string;
    branch: string;
    autoDetect: boolean;
    userParamsData: object;
    mode: RunnerMode;
    canary?: boolean;
    rerunFailedByRunId?: string;
    disableTimeoutRetry?: boolean;
    resultLabels: string[];
    path?: string;
    protocol?: string;
    testobjectSauce: { testobjectApiKey?: string };
    overrideExecutionName?: string;
    passZeroTests: boolean;
    runQuarantinedTests: boolean;
    downloadBrowser: boolean;
    disableSockets: boolean;
    disableCookiesSameSiteNoneRequiresSecure: boolean;
    shouldMonitorPerformance?: boolean;
    exitCodeIgnoreFailingTests?: boolean;
    seleniumCapsFileContent: object;
    retentionDays?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
    retries?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;
    runParams?: { [testResultId: string]: object | undefined };
    publicIps?: Array<string | undefined>; // public IPs of egress requests discovered by our server or 3rd party mirroring services

    // #region mobile flags
    deviceName?: string;
    deviceUdid?: string;
    customTag?: string;
    osVersion?: string;
    duration?: number;
    appId?: string;
    skipLoadBalancer: boolean;
    lockDevice: boolean;
    appiumLogLevel: AppiumLogLevel;
    fullReset?: boolean;
    setNoReset: boolean;
    // #endregion

    // #region What to execute
    testId: string[];
    name: string[];
    label: string[];
    suites: string[];
    suiteIds: string[];
    testPlan: string[];
    testPlanIds: string[];
    // #endregion

    // #region Test config to use
    testConfigNames?: string[];
    testConfigIds?: string[];
    // #endregion

    // #region Grid details
    /** The grid's name */
    grid?: string;
    gridId?: string;
    host?: string;
    port?: number;
    gridUsername?: string;
    gridPassword?: string;
    localTmaUrl?: string;
    useLocalChromeDriver?: boolean;
    chromeBinaryLocation?: string;
    useChromeLauncher?: boolean;

    disableGridCheck: boolean;
    browser?: 'edge-chromium' | 'firefox' | 'safari' | 'chrome';

    proxyForGrid: string;

    experitestToken?: string;
    saucelabs: SaucelabsOptions;
    perfecto: { securityToken?: string; location?: string };
    browserstack: BrowserstackOptions;
    // #endregion

    // #region Test Optimization
    testOptimization: TestOptimization;
    // #endregion

    // #region Report settings
    reportFile?: string;
    reportFileClassname?: string | true;
    reporters?: string[];
    // #endregion

    // #region Parallel settings
    beforeParallel: number;
    parallel: number;
    afterParallel: number;
    //#endregion

    // #region TMS
    tmsSuppressReporting: boolean;
    tmsRunId?: string;
    tmsCustomFields?: string;
    // #endregion

    // #region Extension debugging
    ext?: string;
    extensionLocation: string[];
    extensionPath?: string;
    // #endregion

    // #region Session Player debugging
    playerLocation: string;
    playerPath?: string;
    playerRequirePath?: string;
    // #endregion

    // #region Tunnel
    tunnel?: boolean;
    externalLambdatestTunnelId?: string;
    externalLambdatestNTLMTunnelUsername: string;
    externalLambdatestNTLMTunnelPassword: string;
    externalLambdatestUseWss?: string;
    externalLambdatestDisableAutomationTunneling: boolean;
    externalLambdatestMitm: boolean;
    // #endregion

    // #region Hooks
    beforeTest?: BeforeTestHook;
    afterTest?: AfterTestHook;
    beforeSuite?: BeforeSuiteHook;
    afterSuite?: AfterSuiteHook;
    // #endregion

    // #region Timeouts
    testStartTimeout: number;
    timeout: number;
    timeoutWasGiven: boolean;
    browserTimeout: number;
    newBrowserWaitTimeout: number;
    getBrowserTimeout: number;
    getBrowserRetries: number;
    getSessionTimeout: number;
    getSessionRetries: number;
    driverRequestTimeout: number;
    driverRequestRetries: number;
    // #endregion

    // #region Mock network
    overrideMappingFile?: string;
    mockNetworkRules?: object;
    disableMockNetwork?: boolean;
    // #endregion

    // #region Code coverage
    codeCoverageUrlFilter?: string;
    collectCodeCoverage?: boolean;
    codeCoverageReportPath?: string;
    codeCoverageSourceMapPath?: string;
    codeCoverageReporter: CodeCoverageReporter[];
    codeCoverageInclude: string[];
    // #endregion

    // #region Remote run options
    executionId?: string;
    remoteRunId?: string;
    schedulerId?: string;
    source?: string;
    resultId?: string;
    // #endregion

    // #region Customer Extension
    installCustomExtension?: string;
    // #endregion

    // #region Capabilities format
    w3cCapabilities: boolean;
    oldCapabilities: boolean;
    // #endregion

    // #region Chrome specific settings
    chromeBlockLocation: boolean;
    chromeUserDataDir: false | string;
    chromeExtraPrefs: object;
    chromeExtraArgs: string[];
    // #endregion

    // #region Lightweight / Turbo Mode
    lightweightMode?: LightweightSettings;
    createPrefechedData?: boolean;
    saveRCALocally?: boolean | string;
    localRCASaver?: string;
    // #endregion

    // #region intersections
    intersections: {
        labels?: string[];
        suiteNames?: string[];
        suiteIds?: string[];
        applyIntersectionsToBeforeAndAfterSections?: boolean;
        labelIntersectionOperand?: Operand;
    };
    // #endregion

    // #region visited URLs
    urls?: string;
    // #endregion

    slotService: 'mongo' | 'redis';

    // #region user custom test data
    testData?: string;
    testDataFileName?: string;
    testDataString?: string;
    // #endregion

    printGrids: boolean;
    printFinalCaps: boolean;
    // custom FF for MV3 extension control
    testimHeadlessExtensionDownloadUrl?: string;

    customCapabilities?: ICapabilities;
    customCapabilitiesName?: string;
}

export type Options = InstallLazyDepsOptions | LoginModeOptions | AgentModeOptions | TunnelOptions | RunnerOptions;
